import React from "react";
import {
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { BsPlusLg, BsTrash3Fill } from "react-icons/bs";

import {
  LimitWindow,
  UNIT_OPTIONS,
  WindowUnit,
  describeLimitValue,
  parseLimitValue,
  serializeLimitWindows,
} from "./rateLimit";

interface PropsInterface {
  windows: Array<LimitWindow>;
  onChange: (windows: Array<LimitWindow>) => void;
  disabled?: boolean;
}

/**
 * Edits one limit as a list of count/window pairs.
 *
 * Stacking is the useful shape here — a tight hourly cap plus a looser daily
 * one — and every window in the list applies simultaneously, which the preview
 * line spells out so it does not read as "whichever matches first".
 */
export const LimitWindowsField: React.FC<PropsInterface> = ({
  windows,
  onChange,
  disabled,
}) => {
  const serialized = serializeLimitWindows(windows);
  const readable = parseLimitValue(serialized) ? describeLimitValue(serialized) : "";

  const update = (index: number, patch: Partial<LimitWindow>) => {
    onChange(
      windows.map((window, position) =>
        position === index ? { ...window, ...patch } : window
      )
    );
  };

  const remove = (index: number) => {
    onChange(windows.filter((_, position) => position !== index));
  };

  const add = () => {
    const last = windows[windows.length - 1];

    // Default the new row to the next coarser unit, which is nearly always
    // what "add another window" is reaching for.
    const nextUnit: WindowUnit =
      last?.unit === "s"
        ? "m"
        : last?.unit === "m"
        ? "h"
        : last?.unit === "h"
        ? "d"
        : "d";

    onChange([...windows, { count: "", amount: "1", unit: nextUnit }]);
  };

  return (
    <Stack spacing={1.5}>
      {windows.map((window, index) => (
        <Stack
          key={index}
          direction="row"
          spacing={1.5}
          alignItems="flex-end"
          flexWrap="wrap"
        >
          <TextField
            label={index === 0 ? "Max" : "and max"}
            type="number"
            size="small"
            disabled={disabled}
            value={window.count}
            onChange={(event) => update(index, { count: event.target.value })}
            inputProps={{ min: 0 }}
            sx={{ width: 110 }}
          />

          <Typography variant="body2" sx={{ pb: 1 }}>
            per
          </Typography>

          <TextField
            label="Every"
            type="number"
            size="small"
            disabled={disabled}
            value={window.amount}
            onChange={(event) => update(index, { amount: event.target.value })}
            inputProps={{ min: 1 }}
            sx={{ width: 110 }}
          />

          <TextField
            select
            label="Unit"
            size="small"
            disabled={disabled}
            value={window.unit}
            onChange={(event) =>
              update(index, { unit: event.target.value as WindowUnit })
            }
            sx={{ width: 130 }}
          >
            {UNIT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          {windows.length > 1 && (
            <Tooltip title="Remove this window">
              <span>
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={() => remove(index)}
                  aria-label="Remove this window"
                >
                  <BsTrash3Fill size={16} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      ))}

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        flexWrap="wrap"
        rowGap={1}
      >
        <Button
          size="small"
          startIcon={<BsPlusLg size={12} />}
          disabled={disabled}
          onClick={add}
        >
          Add another window
        </Button>

        <Typography variant="caption" color="text.secondary">
          {windows.length > 1
            ? "All windows apply at the same time."
            : "Stack a second window for a tight short-term cap plus a looser daily one."}
        </Typography>
      </Stack>

      <Typography variant="caption" color="text.secondary">
        Saves as <code>{serialized || "—"}</code>
        {readable && ` — ${readable}`}
      </Typography>
    </Stack>
  );
};
